// AiAsk — WHAT WAITS ON THE PERSON, wherever the thread is not on screen
// (Nika 2026-10-28: "when AI asks for confirmation, in minimized mode its not
// visible" — the same class in the voice call: "the ask must show in the call
// (Run · Skip)" — and the coordinator's W7 live finding the same day: an
// ask_user question was invisible in the minimized island, which read
// "Answer ready" while the question waited).
//
// Two kinds of ask stop a turn on the person, and the thread asks both in
// place:
//   • an APPROVAL — the client gate parks a write the AI wants to make
//     (simoneAiTransport.runClientToolWithAuth): its tool row turns
//     `status: 'approval'`, and the row's own Run · Skip · Run all answer it;
//   • a QUESTION — ask_user hangs a confirm/choice card on the message
//     (simoneAiTransport askUser), and the thread's ChatCard buttons answer it
//     through resolveChatCard(cardId, value, label).
// This asks everywhere else — ONE component, mounted where a surface hides
// the thread:
//   • above the minimized island and a composer's call strip (AiBubbleStack
//     mounts it nearest the bar);
//   • on the full-screen voice call (VoiceOverlay's column): the island and
//     the strip are hidden while the call screen is up, and the live agent
//     keeps it up for its whole ask, so nothing else would show the ask.
//
//   • AskCard — a group named by the ask's words: "Needs your OK" or
//     "Waiting for your choice", the thread's own. An approval: the step in
//     the tool row's own words, one line of what it will write, and Run ·
//     Skip · Run all on the SAME settle path as the row's own buttons
//     (resolveAiToolApproval / approveAllAiToolsForTurn). A question: its
//     prompt and one button per option — the card's own labels and values, by
//     the ONE rule the thread card reads (functions/askCardOptions) — then
//     the card's two other paths (coordinator F2, 2026-10-28): Write my own
//     (a choice) or Edit (a confirm) opens a field, where Enter or Send
//     answers in the person's own words and Escape goes back. All on the
//     SAME writer as the card's (resolveChatCard, with the card's own
//     `extra`). Off while the thread is on screen (it asks there) — never
//     under the call screen, which covers the thread.
//   • AskAnnounce — a polite live region, always in the tree, that says
//     "Needs your OK: <step>" or "Waiting for your choice: <question>" when an
//     ask appears. Nothing here takes focus on its own (the thread's
//     question does); the field takes it when the person opens it.
//
// The wrapper is `display: contents`: the card and the region sit in the
// host's own flow (the stack's column, the call's column) as if they were its
// children, so an empty ask adds no box and no gap.
//
// Every factory is self-contained and every helper lives in `scope` (FA204 —
// frank serializes each factory on its own). Each helper reads its root keys
// up front, before any branch (FA515: a short-circuit drops a subscription).
export const AiAsk = {
  scope: {
    // The thread the ask reads first: the VOICE thread while a call is open
    // (`voiceChatKey`, the prefix VoiceLog reads — a call can run in the
    // add-app or the dashboard thread), else the general one.
    threadKey(root) {
      const r = root || {}
      const open = r.voiceOpen
      const key = r.voiceChatKey
      return open && typeof key === 'string' ? key : ''
    },

    // What waits on the person in one thread, or null — the newest ask among
    // the assistant messages after the person's last line (a new line closes
    // both kinds: simoneAiTransport.supersedeParkedAiToolApprovals):
    // { kind: 'approval', tool } — a step parked for the OK — or
    // { kind: 'choice', card } — a question's card nobody answered yet.
    // Read from the thread, not from the live id: a late ask lands on the
    // newest assistant message after the turn's id cleared
    // (simoneAiTransport._patchToolActivity), and it still holds the server's
    // loop. Mirrors SimoneReasoningPill.scope.approvalTool / choiceCard (the
    // island's head) and VoiceOverlay.scope.heldAsk (the voice says it).
    waitingIn(msgs) {
      const list = Array.isArray(msgs) ? msgs : []
      for (let i = list.length - 1; i >= 0; i--) {
        const m = list[i]
        if (!m) continue
        if (m.role === 'user') return null
        if (m.role !== 'assistant') continue
        const tools = Array.isArray(m.tools) ? m.tools : []
        for (let j = tools.length - 1; j >= 0; j--) {
          if (tools[j] && tools[j].status === 'approval')
            return { kind: 'approval', tool: tools[j] }
        }
        const cards = Array.isArray(m.cards) ? m.cards : []
        for (let j = cards.length - 1; j >= 0; j--) {
          const c = cards[j]
          const asks = !!c && (c.type === 'choice' || c.type === 'confirm')
          if (asks && c._cardId && !c._resolved) return { kind: 'choice', card: c }
        }
      }
      return null
    },

    // The ask: in the call's thread first, then the general one.
    pending(root) {
      const r = root || {}
      const k = this.threadKey(r)
      const own = this.waitingIn(r[k + 'chatMessages'])
      const general = k ? this.waitingIn(r.chatMessages) : null
      return own || general
    },

    approvalTool(root) {
      const ask = this.pending(root)
      return ask && ask.kind === 'approval' ? ask.tool : null
    },

    choiceCard(root) {
      const ask = this.pending(root)
      return ask && ask.kind === 'choice' ? ask.card : null
    },

    // The thread is on screen — the drawer docked open, or the main thread —
    // and it already asks there. Never while the full-screen call is up: the
    // call covers the thread.
    threadOnScreen(root) {
      const r = root || {}
      const open = r.voiceOpen
      const minimized = r.voiceMinimized
      const host = r.voiceHost
      const drawer = r.assistantOpen
      const dock = r.assistantDock
      const home = r._homeThreadOpen
      if (open && !minimized && !host) return false
      return !!((drawer && dock !== 'minimized') || home)
    },

    askShown(root) {
      const ask = this.pending(root)
      const covered = this.threadOnScreen(root)
      return !!ask && !covered
    },

    // The ask's words — the thread's own: "Needs your OK" for a step
    // (ChatMessage, the island's head), "Waiting for your choice" for a
    // question (the ask_user tool row).
    askWord(el, root) {
      void (root && root.lang)
      const card = this.choiceCard(root)
      return el.call('polyglot', card ? 'ai.toolStep.choose' : 'ai.chat.needsOk')
    },

    // The question in its own words (the card's prompt), or ''.
    promptOf(root) {
      const card = this.choiceCard(root)
      return card && typeof card.prompt === 'string' ? card.prompt.trim() : ''
    },

    // What the live region says first: the ask's words, and a colon when the
    // step or the question follows them.
    announceHead(el, root) {
      const shown = this.askShown(root)
      const tool = this.approvalTool(root)
      const prompt = this.promptOf(root)
      if (!shown) return ''
      const word = this.askWord(el, root)
      return tool || prompt ? word + ': ' : word
    },

    // One line of what the step will write: the first line of its body, else
    // its title. Positional arguments (an app verb) give their first text.
    preview(tool) {
      const a = tool && tool.args
      if (!a || typeof a !== 'object') return ''
      const first = (v) => {
        if (typeof v !== 'string') return ''
        const line = v
          .split('\n')
          .map((x) => x.replace(/^[\s#>*-]+/, '').trim())
          .find(Boolean)
        return line ? (line.length > 160 ? line.slice(0, 159) + '…' : line) : ''
      }
      const list = Array.isArray(a) ? a : Array.isArray(a.args) ? a.args : null
      if (list) {
        for (const v of list) {
          const line = first(v)
          if (line) return line
        }
        return ''
      }
      const keys = ['content', 'body', 'text', 'description', 'message', 'prompt']
      const names = ['title', 'name', 'summary', 'query', 'subject']
      for (const k of keys.concat(names)) {
        const line = first(a[k])
        if (line) return line
      }
      return ''
    },

    // The step's glyph: a document for what writes a page, a check for a
    // ticket, the assistant's star for anything else.
    glyph(tool) {
      const name = String((tool && tool.tool) || '').replace(/^module_fn_/, '')
      if (/note|doc|folder|page/i.test(name)) return 'document'
      if (/ticket/i.test(name)) return 'check'
      return 'magicstar'
    },

    // The ask's glyph: a question mark for a question, the step's own for an
    // approval.
    askGlyph(root) {
      const card = this.choiceCard(root)
      const tool = this.approvalTool(root)
      return card ? 'questionMark' : this.glyph(tool)
    },

    // The question's buttons, by the ONE rule the thread's card reads
    // (functions/askCardOptions, through el.call): a confirm card is its two
    // labels — the card's own, else Confirm / Cancel — the first one primary;
    // a choice card is its options in their order, each answering with its
    // value (else its label). Each row carries the card's id for the writer.
    askOptions(el, root) {
      void (root && root.lang)
      const card = this.choiceCard(root)
      if (!card) return []
      const rows = el.call('askCardOptions', card)
      return (Array.isArray(rows) ? rows : []).map((o) => ({ ...o, cardId: card._cardId }))
    },

    // Run / Skip — the row's own settle. Guarded: a host without the shell's
    // function barrel (the standalone canvas) has no ask to answer.
    settle(el, s, approved) {
      const tool = this.approvalTool(s && s.root)
      const fns = (el.context && el.context.functions) || {}
      if (!tool || typeof fns.resolveAiToolApproval !== 'function') return
      el.call('resolveAiToolApproval', tool.id, !!approved)
      this.askAnswered(el, s.root)
    },

    // Run all — this turn only (the transport keys the grant to the turn).
    approveAll(el) {
      const fns = (el.context && el.context.functions) || {}
      if (typeof fns.approveAllAiToolsForTurn !== 'function') return
      el.call('approveAllAiToolsForTurn')
      this.askAnswered(el, el.state && el.state.root)
    },

    // AFTER AN ANSWER, FOCUS STAYS ON THE SURFACE (coordinator W3c,
    // 2026-10-29): the ask hides once it is answered, and the control that
    // had focus goes with it — focus fell to the page's body. It goes to the
    // text input of the same surface: the composer the island docks above
    // (or the one whose call strip holds the ask) — the shell's own path,
    // voiceFocusComposer, found by key. The full-screen call has no text
    // input: there it goes to the call's Minimize, where the call screen puts
    // focus when it opens (VoiceOverlay.scope.syncFocus). The call is told
    // by the posture threadOnScreen reads, never by `lookup`: that also
    // returns a root's child of the same key, from anywhere in the tree.
    // A composer that HOSTS the call shows no text input either: its strip
    // stands in the field's place (VoiceStrip.js), so focus goes to the
    // strip's Type — VoiceControls' KeyboardBtn, "give me the text input
    // back" (coordinator decision, 2026-10-29). The posture says a strip is
    // up; `lookup` from the ask finds the strip it floats over. A phone's
    // strip has no Type (the call hands itself to the island there after
    // its first line), so the composer path stays.
    askAnswered(el, root) {
      const r = root || {}
      const open = r.voiceOpen
      const minimized = r.voiceMinimized
      const host = r.voiceHost
      if (open && !minimized && !host) {
        const shell = typeof el.getRoot === 'function' ? el.getRoot() : null
        const call = shell && shell.lookdown ? shell.lookdown('VoiceOverlay') : null
        const back = call && call.lookdown ? call.lookdown('CallMinimize') : null
        queueMicrotask(() => {
          const node = back && back.node
          if (node && node.isConnected && typeof node.focus === 'function') node.focus()
        })
        return
      }
      if (open && host && r._isPhoneViewport !== true) {
        const strip = typeof el.lookup === 'function' ? el.lookup('VoiceStrip') : null
        const type = strip && strip.lookdown ? strip.lookdown('KeyboardBtn') : null
        if (type) {
          queueMicrotask(() => {
            const node = type.node
            if (node && node.isConnected && typeof node.focus === 'function') node.focus()
          })
          return
        }
      }
      const fns = (el.context && el.context.functions) || {}
      if (typeof fns.voiceFocusComposer === 'function') el.call('voiceFocusComposer')
    },

    // A question's answer — the card's own writer, as the thread's ChatCard
    // buttons call it: the card's id, the option's value and its label (the
    // transport resumes the suspended turn with them). Guarded like settle.
    // A pick closes an open field first: its words were never sent, and a
    // hidden field that holds words reads as unsaved work to the shell
    // (ChatCard.scope.askPick does the same).
    choose(el, s) {
      const root = s && s.root
      const fns = (el.context && el.context.functions) || {}
      if (!s || !s.cardId || typeof fns.resolveChatCard !== 'function') return
      if (this.askMode(root)) this.askSetMode(root, null)
      el.call('resolveChatCard', s.cardId, s.value, s.label)
      this.askAnswered(el, root)
    },

    // ── WRITE MY OWN / EDIT (coordinator F2, 2026-10-28: on the call screen
    // and the island a typed message closed the question instead of
    // answering it). The thread card's two other paths (ChatCard PvAnswers /
    // PvCompose), in its words and on its writer. The open field lives where
    // the card keeps its own: the root's `_aiAskCompose`, by card id
    // (`{ field: 'own' | 'edit', change: false, text }`, UI state only), so
    // the drawer and this ask share ONE open field per question.
    askMode(root) {
      const all = root && root._aiAskCompose
      const card = this.choiceCard(root)
      const id = card && card._cardId
      const mode = all && id ? all[id] : null
      return mode && typeof mode === 'object' ? mode : null
    },

    // The field open on the ask: 'own' (Write my own) or 'edit' (a confirm
    // card's Edit), else null.
    askField(root) {
      const mode = this.askMode(root)
      const field = mode && mode.field
      return field === 'own' || field === 'edit' ? field : null
    },

    // The field's words, bound to its `value`, while the field is open: a
    // mode that closes takes them with it, and a question that closes (its
    // turn stopped or ended, or the drawer answered it) is no longer read,
    // so a closed field is empty — no unsaved work left behind.
    askText(root) {
      const mode = this.askMode(root)
      const open = this.askField(root)
      return open && mode && typeof mode.text === 'string' ? mode.text : ''
    },

    // Write the question's mode (null clears it) — in memory only, a new
    // object each time (ChatCard.scope.askSetMode).
    askSetMode(root, mode) {
      const card = this.choiceCard(root)
      const id = card && card._cardId
      if (!root || typeof root.update !== 'function' || !id) return
      const all = { ...(root._aiAskCompose || {}) }
      if (mode) all[id] = mode
      else delete all[id]
      root.update({ _aiAskCompose: all }, { preventFetch: true })
    },

    askSetText(root, text) {
      const mode = this.askMode(root)
      if (!mode || !mode.field) return
      this.askSetMode(root, { ...mode, text: String(text == null ? '' : text) })
    },

    // The ask's card around any of its controls, and its controls by role:
    // 'field' (the input), 'writeOwn' (Write my own / Edit).
    askCardOf(el) {
      if (!el) return null
      if (el.key === 'AskCard') return el
      return typeof el.lookup === 'function' ? el.lookup('AskCard') : null
    },

    askTarget(card, name) {
      if (!card) return null
      if (name === 'field') return (card.AskCompose && card.AskCompose.AppInputCompact) || null
      if (name === 'writeOwn') {
        return (card.AskOptions && card.AskOptions.AppCapsuleBtnGhost_own) || null
      }
      return null
    },

    // Focus a control once the update shows it: `hide` applies in the
    // update's microtask, so a focus in the same tick lands on a hidden node;
    // two microtasks later is still inside the press, so a phone keyboard
    // opens for the field (ChatCard.scope.askFocus).
    askFocus(el, name) {
      const card = this.askCardOf(el)
      if (!card) return
      const run = () => {
        const target = this.askTarget(card, name)
        const node = target && target.node
        if (node && typeof node.focus === 'function') node.focus()
      }
      queueMicrotask(() => queueMicrotask(run))
    },

    // Write my own (a choice) / Edit (a confirm): the field opens under the
    // answers, empty, with focus.
    askOpenField(el, root) {
      const card = this.choiceCard(root)
      if (!card) return
      const field = card.type === 'confirm' ? 'edit' : 'own'
      this.askSetMode(root, { field, change: false, text: '' })
      this.askFocus(el, 'field')
    },

    // The field's close (and Escape): back to the answers, its words
    // dropped, focus on the control that opened it.
    askCloseField(el, root) {
      this.askSetMode(root, null)
      this.askFocus(el, 'writeOwn')
    },

    // Send the field — the card's own writer with the card's own `extra`:
    // the person's words as the answer (Write my own), or what should change
    // (Edit: never a yes — the transport answers the tool `confirmed: false`
    // with the words). An empty field sends nothing and keeps the keyboard.
    // Guarded like choose.
    askSubmit(el, root) {
      const card = this.choiceCard(root)
      const kind = this.askField(root)
      const field = this.askTarget(this.askCardOf(el), 'field')
      const node = field && field.node
      const fns = (el.context && el.context.functions) || {}
      if (!card || !card._cardId || !kind || typeof fns.resolveChatCard !== 'function') return
      // the words on screen (the last keystroke may not have reached the
      // mode yet), else the mode's
      const typed = node && typeof node.value === 'string' ? node.value : this.askText(root)
      const text = String(typed || '').trim()
      if (!text) {
        this.askFocus(el, 'field')
        return
      }
      const extra = kind === 'edit' ? { edit: true, text } : { written: true, text }
      this.askSetMode(root, null)
      el.call('resolveChatCard', card._cardId, kind === 'edit' ? 'edit' : text, text, extra)
      this.askAnswered(el, root)
    }
  },

  display: 'contents',

  AskCard: {
    hide: (el, s) => !el.scope.askShown(s.root),
    role: 'group',
    aria: { label: (el, s) => el.scope.askWord(el, s.root) },
    // The host's full column width: the stack stretches its rows, the call's
    // column centres its own.
    alignSelf: 'stretch',
    flow: 'y',
    gap: 'Z',
    padding: 'Z1',
    theme: 'dialog',
    shadow: 'float',
    round: 'radiusCard',
    borderWidth: '0',
    textAlign: 'left',
    pointerEvents: 'auto',
    animationName: 'fadeInUpShort',
    animationDuration: 'B',

    AskHead: {
      flow: 'x',
      align: 'center flex-start',
      gap: 'Y',
      minWidth: '0',
      Icon: {
        name: (el, s) => el.scope.askGlyph(s.root),
        boxSize: 'A',
        color: 'title',
        flexShrink: '0'
      },
      // An approval: the step in the tool row's own words — primary text, so
      // it wraps.
      AskStep: {
        hide: (el, s) => !el.scope.approvalTool(s.root),
        flex: '1 1 auto',
        minWidth: '0',
        fontSize: 'Z1',
        fontWeight: '500',
        lineHeight: '1.45',
        color: 'title',
        childExtends: 'ChatToolStepLabel',
        childrenAs: 'state',
        children: (el, s) => {
          const tool = el.scope.approvalTool(s.root)
          return tool ? [{ ...tool, key: 'ask-' + tool.id }] : []
        }
      },
      // A question: its prompt, whole — primary text, so it wraps. A card
      // with no prompt names itself by the ask's words.
      AskPrompt: {
        tag: 'span',
        hide: (el, s) => !el.scope.choiceCard(s.root),
        flex: '1 1 auto',
        minWidth: '0',
        fontSize: 'Z1',
        fontWeight: '500',
        lineHeight: '1.45',
        color: 'title',
        text: (el, s) => el.scope.promptOf(s.root) || el.scope.askWord(el, s.root)
      }
    },

    AskPreview: {
      tag: 'span',
      hide: (el, s) => !el.scope.preview(el.scope.approvalTool(s.root)),
      minWidth: '0',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontSize: 'Z1',
      color: 'paragraph',
      text: (el, s) => el.scope.preview(el.scope.approvalTool(s.root)),
      title: (el, s) => el.scope.preview(el.scope.approvalTool(s.root))
    },

    AskActions: {
      hide: (el, s) => !el.scope.approvalTool(s.root),
      flow: 'x',
      align: 'center flex-start',
      gap: 'Y',
      flexWrap: 'wrap',
      AppCapsuleBtnPrimary_run: {
        compact: true,
        text: '{{ ai.tool.run | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.scope.settle(el, s, true)
        }
      },
      AppCapsuleBtnGhost_skip: {
        compact: true,
        text: '{{ ai.tool.skip | polyglot }}',
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.scope.settle(el, s, false)
        }
      },
      AppCapsuleBtnGhost_all: {
        compact: true,
        text: '{{ ai.tool.runAll | polyglot }}',
        title: (el) => el.call('polyglot', 'ai.tool.runAllTooltip'),
        onClick: (e, el) => {
          e.stopPropagation()
          el.scope.approveAll(el)
        }
      }
    },

    // A question's answers in one wrapping row, as the thread card lays them
    // out (ChatCard PvAnswers): its options, then Write my own / Edit.
    AskOptions: {
      hide: (el, s) => !el.scope.choiceCard(s.root),
      flow: 'x',
      align: 'center flex-start',
      gap: 'Y',
      flexWrap: 'wrap',
      // One capsule per option in the card's order: the ink capsule for a
      // primary option (a confirm's yes), the quiet one for the rest — the
      // pair Run · Skip use. They sit in the row itself (no box of their
      // own), so they wrap with Write my own as one row. Each row's own state
      // is its option; each branch names its capsule as a literal (FA807).
      AskPicks: {
        display: 'contents',
        children: (el, s) =>
          el.scope
            .askOptions(el, s.root)
            .map((o) =>
              o.primary
                ? { key: 'opt-' + o.cardId + '-' + o.at, extends: 'AppCapsuleBtnPrimary', state: o }
                : { key: 'opt-' + o.cardId + '-' + o.at, extends: 'AppCapsuleBtnGhost', state: o }
            ),
        childProps: {
          compact: true,
          text: (el, s) => s.label,
          onClick: (e, el, s) => {
            e.stopPropagation()
            el.scope.choose(el, s)
          }
        }
      },
      // Write my own (a choice card) / Edit (a confirm card): opens the
      // field, and gives way while it is open. Function text beside the
      // Icon: a `{{ }}` label is rewritten by the language switch through
      // textContent, which drops the Icon (ChatCard's own note).
      AppCapsuleBtnGhost_own: {
        compact: true,
        hide: (el, s) => !!el.scope.askField(s.root),
        Icon: { name: 'edit', fontSize: 'Z2', flexShrink: '0' },
        Label: {
          tag: 'span',
          text: (el, s) => {
            void s.root?.lang
            const card = el.scope.choiceCard(s.root)
            return el.call(
              'polyglot',
              card && card.type === 'confirm' ? 'common.action.edit' : 'ai.chatCard.ask.writeOwn'
            )
          }
        },
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          el.scope.askOpenField(el, s.root)
        }
      }
    },

    // The field: the person's own words (Write my own) or what should change
    // (Edit). Enter or Send sends; Escape and the close go back. Escape stays
    // in the field: past it, on the full-screen call the window's Escape
    // steps the call down to the island (VoiceOverlay.onWindowKeydown), and
    // in the shell it closes what the page has open.
    AskCompose: {
      hide: (el, s) => !el.scope.askField(s.root),
      flow: 'x',
      align: 'center',
      gap: 'Y',
      width: '100%',
      minWidth: '0',
      // AppInput carries a scope of its own (the field kit), so its factories
      // reach the ask's helpers through the row. Its words are bound to the
      // question's mode (scope.askText): a mode that closes empties it.
      AppInputCompact: {
        flex: '1 1 0',
        enterkeyhint: 'send',
        autocomplete: 'off',
        value: (el, s) => el.parent.scope.askText(s.root),
        onInput: (e, el, s) => el.parent.scope.askSetText(s.root, el.node.value),
        placeholder: (el, s) => {
          void s.root?.lang
          return el.call(
            'polyglot',
            el.parent.scope.askField(s.root) === 'edit'
              ? 'ai.chatCard.ask.editPlaceholder'
              : 'ai.chatCard.ask.ownPlaceholder'
          )
        },
        ariaLabel: (el, s) => {
          void s.root?.lang
          return el.call(
            'polyglot',
            el.parent.scope.askField(s.root) === 'edit'
              ? 'ai.chatCard.ask.editPlaceholder'
              : 'ai.chatCard.ask.ownPlaceholder'
          )
        },
        onKeydown: (e, el, s) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
            e.preventDefault()
            el.parent.scope.askSubmit(el, s.root)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            el.parent.scope.askCloseField(el, s.root)
          }
        }
      },
      AppCapsuleBtnPrimary: {
        compact: true,
        text: '{{ common.action.send | polyglot }}',
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          el.scope.askSubmit(el, s.root)
        }
      },
      // CloseButton carries a scope of its own (its localized name).
      CloseButton: {
        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          el.parent.scope.askCloseField(el, s.root)
        }
      }
    }
  },

  // Visually hidden, never display:none — a live region must stay in the
  // accessibility tree to announce: the sr-only clip box (PageSrTitle's
  // idiom) on the brand's one-pixel size token.
  AskAnnounce: {
    tag: 'span',
    role: 'status',
    aria: { live: 'polite' },
    position: 'absolute',
    width: 'hairline',
    height: 'hairline',
    padding: '0',
    border: '0',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    AskAnnounceWord: {
      tag: 'span',
      text: (el, s) => el.scope.announceHead(el, s.root)
    },
    AskAnnounceStep: {
      tag: 'span',
      childExtends: 'ChatToolStepLabel',
      childrenAs: 'state',
      children: (el, s) => {
        const shown = el.scope.askShown(s.root)
        const tool = el.scope.approvalTool(s.root)
        return shown && tool ? [{ ...tool, key: 'say-' + tool.id }] : []
      }
    },
    AskAnnounceQuestion: {
      tag: 'span',
      text: (el, s) => {
        const shown = el.scope.askShown(s.root)
        const prompt = el.scope.promptOf(s.root)
        return shown ? prompt : ''
      }
    }
  }
}
